import React from 'react';
import { theme, tintOf } from '../theme';
import { CheckIcon, InsteadIcon } from './icons';
import { SMALL_FACT_METRICS } from './SplitPill';
import { isDecisionClaimKind } from '../services/draftClaims';
import type { DraftKind } from '../serverTypes';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { type SecondaryPillVariant } from './SecondaryAxisPills';
import { formatRowDate } from '../utils/formatting';
import { approvedWord, approvedKindWord } from '../services/pressVerdict';
import { SkeletonBlock } from './SkeletonBlock';

// <purpose>THE decision badge (small facts on a row, round 4 A.4, operator 2026-10-19): the clerk's
// primary-axis word, as the primary axis's own squared filled badge, split into an ICON KEY (the
// act: a check for approved-as, the instead-arrow for declined-instead) on a 22 % tint of the hue
// with the hue as ink, and the served WORD white on the hue. One component for the two acts that
// used to wear two faces (WIM's outlined resolution pill and the info pill „Stattdessen: <word>").
// Last element right on row 3, on the detail's decision line and on the Archiv row.
// Registry: design/design-languages.html, entry `primary_axis.decision`.</purpose>
// <dependencies>tintOf (theme) derives the key ground from the hue — never a second token. The hue
// itself is the served hue of the chosen word (cm.color). A decline whose word is the chooser's own
// (Von Hand beantwortet, Kein Vorgang) or absent has NO hue: it wears the OUTLINED face (white
// ground, navy ink and border). On a gMSB tenant every axis hue is a K tier, so a borrowed fill
// (navy-light #555E99 sat beside K3A #4A517F) read as a tier the clerk never chose; no axis value
// wears the outline. Operator ruling 2026-10-27 (B5 walk).</dependencies>
// <constraints>A decline without a word renders the key segment ALONE (no empty value segment, no
// divider). No hover: the word is the long form (house rule: hover only when it reduces load).
// Fixed height per variant so the badge landing changes no row height.</constraints>
export type DecisionAct = 'approved' | 'instead';
export type DecisionBadgeVariant = 'row' | 'detail';

/** The share of the hue in the key segment's ground (the rest is white). */
export const DECISION_KEY_TINT = 0.22;

// Size, icon and height are the small-fact metrics the split pill shares (SMALL_FACT_METRICS): one
// table, so the badge and the traces beside it on row 3 can never end up at two heights.
const METRICS: Record<DecisionBadgeVariant, { fontSize: number; keyPadding: string; valuePadding: string; iconSize: number; height: number }> = {
  row:    { ...SMALL_FACT_METRICS.row, keyPadding: '0 5px', valuePadding: '0 6px' },
  detail: { ...SMALL_FACT_METRICS.detail, keyPadding: '0 6px', valuePadding: '0 8px' },
};

export const DecisionBadge: React.FC<{
  act: DecisionAct;
  /** The served hue the badge wears — the chosen word's own colour; null = the outlined face (a
   *  word that is not an axis value, so it has no colour of its own). */
  hue: string | null;
  /** The served word, or null for a decline filed without one (the key segment alone). */
  word: string | null;
  /** The accessible name of the KEY-ONLY form (no word to read): the verdict's own word, e.g.
   *  „Nicht hilfreich". Ignored when a word is shown — the word is the name. */
  label?: string;
  variant?: DecisionBadgeVariant;
  testId?: string;
  style?: React.CSSProperties;
}> = ({ act, hue, word, label, variant = 'row', testId, style }) => {
  const m = METRICS[variant];
  const outlined = hue === null;
  const ink = outlined ? theme.colors.navy : hue;
  return (
    <span
      data-testid={testId}
      data-act={act}
      role={word ? undefined : 'img'}
      aria-label={word ? undefined : label}
      style={{
        display: 'inline-flex', alignItems: 'stretch', boxSizing: 'border-box',
        height: m.height, borderRadius: 4, overflow: 'hidden',
        // The outline is drawn inside the box (boxSizing above), so both faces keep one height.
        border: outlined ? `1px solid ${ink}` : undefined, backgroundColor: outlined ? theme.colors.white : undefined,
        fontSize: m.fontSize, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0,
        ...style,
      }}
    >
      <span
        style={{ display: 'flex', alignItems: 'center', padding: m.keyPadding, backgroundColor: outlined ? undefined : tintOf(ink, DECISION_KEY_TINT), color: ink }}
      >
        {act === 'approved' ? <CheckIcon stamp size={m.iconSize} color={ink} /> : <InsteadIcon size={m.iconSize} color={ink} />}
      </span>
      {word !== null && word !== '' && (
        <span style={outlined
          ? { display: 'inline-flex', alignItems: 'center', padding: m.valuePadding, borderLeft: `1px solid ${ink}`, color: ink }
          : { display: 'inline-flex', alignItems: 'center', padding: m.valuePadding, backgroundColor: ink, color: theme.colors.white }}>
          {word}
        </span>
      )}
    </span>
  );
};

// THE DECISION BADGE — the head of ROW 3's right-hand Bearbeitung group (ROW COMPOSITION v1, part 7
// fix loop, 2026-10-12), and the whole of what the row says about the clerk's decision
// (operator rulings R31/R32, 2026-09-18, amended by the row-grammar ruling of the same date, and
// again by the row-grammar ruling of 2026-09-24, R34, and by the press of 2026-10-28): the badge
// says the clerk APPROVED. On a tenant that states more than one Bearbeitung
// (`cm.statesApprovedKind`) it names the approved kind — „Weiterleitung genehmigt", the served kind
// word in fixed German — in that kind's handling hue. On every other tenant (gMSB/wMSB/VNB today)
// it reads „Genehmigt" in the row's classification hue: the clerk approved the classified answer.
// Only FORMAL approvals count (approvedRowKinds / the detail's approve claims): a copy is the
// „Kopiert" trace and never a „genehmigt". The DETAIL dates the decision; a list row never does.
//
// Three faces retired into this one (all of them words this app chose rather than the tenant):
//   „Genehmigt"/„Kopiert" — the R20 status split, which existed only because the pill repeated the
//     category badge's word whenever the approved kind matched the classified one. Stating the SAME
//     served word twice (R34) makes that repetition the honest reading rather than a fault to
//     rename around, so the split — and its „Genehmigt" fallback for a silent vocabulary — has
//     nothing left to solve. No fallback word exists for this badge any more, on any tenant.
//   „<Wert> (Kopie)" — the indirect-approval suffix. Row 3's „Kopiert" badge already states that,
//     and a decision reads the same whichever gesture made it.
//   „empfangsbestätigt" — the ack override (CLAIM_KIND_PILL_FACE, deleted). Under R31 the receipt
//     is NOT a decision: it carries no badge here at all, and its signal is row 3's
//     „Eingang bestätigt".
//
// The face resolves through ONE seam, exactly as the hue does: the served `decisionKindHandling`
// (`useCategoryMeta().decisionKindHandling`, part 4c — was the code constant `CLAIM_KIND_HANDLING`)
// maps the claimed kind to a handling id, and cm.label/cm.color answer for it — so shared.tsx still
// spells no handling id, and re-wording a handling's face stays a seed edit everywhere it appears.
// Two kinds sharing a handling therefore share a face, which is the honest reading: the badge
// states what the decision resolved to, not which button made it. The HOVER names the approved
// label always, and the classified one too whenever it differs — „Vom Sachbearbeiter entschieden:
// Antworten (klassifiziert: Weiterleiten)" — so a clerk hovering an equality badge sees no bracket
// at all, and a clerk hovering a deviation learns both words without leaving the row.
//
// The guard AND the type proof for a claim kind off the wire (string[], not DraftKind[]). The
// served map's own keys are the only kinds this surface recognises — an ABSENT map (a generation-9
// backend, or a test double that passes none) recognises NOTHING, which is the honest reading: no
// guessed badge (Review Focus 3). A plain `kind in map` narrows nothing for a Record-typed target
// (no index signature) — a declared type predicate is what actually narrows `string` to `DraftKind`
// here.
function isResolvablePillKind(kind: string, map: Readonly<Record<string, string>>): kind is DraftKind {
  return Object.prototype.hasOwnProperty.call(map, kind);
}

/** THE decision kinds on a row, in served order — the one resolution DecisionBadges renders from.
 *  An unknown kind (or every kind, on an absent `decisionKindHandling`) contributes nothing rather
 *  than crashing or showing a raw id, and `ack` contributes nothing by RULING (isDecisionClaimKind): a
 *  receipt is not a decision. */
const decisionKindsOf = (claims: string[] | null | undefined, kindHandling: Readonly<Record<string, string>>): DraftKind[] =>
  (claims ?? []).filter((kind): kind is DraftKind => isResolvablePillKind(kind, kindHandling)).filter(isDecisionClaimKind);

// (`hasDecisionBadge`, the exported twin of decisionKindsOf, is DELETED with ruling R33a: the
// sender pill was its one reader, and the pill now states the REGISTRY fact rather than the
// ticket's approval. The decision badge is the only approval signal on the row again, so the predicate
// has exactly one reader and does not need to be one.)

// The loading shimmer's box. HEIGHT is the badge's own painted height: a 10px line box plus 1px of
// padding top and bottom — the 1px border rides inside it via boxSizing. 16 is that figure rounded
// up, and it is safe to round up in one direction only: both pill rows below are `minHeight: 18`
// with `alignItems: 'center'`, so any box under 18 costs the row no height at all — which is what
// makes the swap vertically free, not the exactness of this number.
// WIDTH is a plain estimate again, and allowed to be: the badge heads row 3's right-aligned
// Bearbeitung group (since 2026-10-12), so the shimmer settling to the word's real width grows the
// group leftward into free space and pushes nothing sideways. That is the same fact that retired the MEASURED slot before this ruling
// — a reservation with nothing to protect is dead weight on every row in the fleet.
const RESOLUTION_PILL_HEIGHT = 18;
const RESOLUTION_PILL_SKELETON_WIDTH = 72;

/** THE DECISION — the head of row 3's Bearbeitung group, mounted identically by the list row and the detail (and by
 *  the Archiv ledger's compact line). One component rather than two renders of the same rule: a
 *  clerk who opens a row must not find the detail saying something different from the row they
 *  clicked, and a surface that re-implements the face logic is exactly how that drift starts — the
 *  same reason TicketAttributePills exists. The DATE rides here for that same reason: a date beside
 *  a badge is one statement, and two surfaces composing it from their own halves is how one of them
 *  ends up dating a decision the other does not.
 *
 *  ⚠ ONLY THE DETAIL DATES A DECISION since ruling R33 (2026-09-19): the dates left the list rows,
 *  so every row mount passes no `decidedAt` at all and a multi-kind row's badge stands alone there.
 *  The prop stays because the detail's decision line still carries the stamp — and it stays HERE,
 *  rather than in the detail, for the reason above.
 *
 *  Returns a FRAGMENT, not a wrapper: both call sites are flex rows whose gap and hierarchy belong
 *  to them, and a div here would put a box around a group their own layout already owns.
 *
 *  `claims` is the row's `draftClaims`, untyped past the network boundary (string[], not
 *  DraftKind[]) — see isResolvablePillKind. `decidedAt` is the stamp each surface resolves from
 *  the shape it holds (services/draftClaims.decisionClaimAt, the detail's keyed claims — a list row
 *  passes none at all); absent/null states the decision badge UNDATED rather than inventing one.
 *  `primaryAxis` is the row's classified
 *  handling, read only to word the hover's „(klassifiziert: …)" clause on a deviation.
 *
 *  NOTHING when there is no decision on a MULTI-kind tenant — no empty reservation, by ruling
 *  (2026-09-18): the badge sits inside row 3's right-aligned Bearbeitung group, so a
 *  decision landing shifts nothing and there is nothing to reserve against. Row 3 keeps its height
 *  from its own minHeight. A SINGLE-kind tenant's approval reads „Genehmigt" (see the component doc
 *  above).
 *
 *  ⚠ WHICH of those two arms applies is itself unknown while `cm.configLoading` is true (the served
 *  `decisionKindHandling` floors to `{}` until /config settles, so `cm.statesApprovedKind` cannot be
 *  trusted yet) — the function therefore asks THAT question first, off `claims` alone, before
 *  either arm gets to ask its own (see the loading gate below). */

// The DECISION's own date — extracted (final review, minor) from the two arms below that each drew
// an identical span: the single-kind arm (the DETAIL's decision date, alone, no badge beside it —
// that IS the single-kind grammar) and the multi-kind arm's date-before-badge pairing (which DOES always have
// a badge beside it, since it only renders inside the `badges.length > 0` branch). One component,
// one footprint, so the two can never drift into two sizes for the same fact.
const DecisionDateSpan: React.FC<{ decidedAt: string; variant?: SecondaryPillVariant }> = ({ decidedAt, variant }) => (
  <span
    data-testid="decision-date"
    style={{
      fontSize: variant === 'detail' ? 13 : 11,
      color: variant === 'detail' ? theme.colors.textSecondary : theme.colors.navyMuted,
      flexShrink: 0,
    }}
  >
    {formatRowDate(decidedAt)}
  </span>
);

/** The kinds a LIST ROW's clerk formally approved: its claims minus the ones made by copying (a kind
 *  missing from the via map reads as approve, the legacy posture — see TicketListItem). */
export const approvedRowKinds = (t: { draftClaims?: readonly string[] | null; draftClaimVia?: Record<string, string> | null }): string[] =>
  (t.draftClaims ?? []).filter(kind => t.draftClaimVia?.[kind] !== 'copy');

export const DecisionBadges: React.FC<{
  /** The kinds the clerk APPROVED (formal approvals only — a copy is the „Kopiert" trace, never a
   *  „genehmigt"; see approvedRowKinds / approvedClaimKinds). */
  claims?: string[] | null;
  decidedAt?: string | null;
  variant?: SecondaryPillVariant;
  /** The row's classification — the hue a one-kind tenant's „Genehmigt" wears (the clerk approved
   *  the classified answer). */
  primaryAxis?: string | null;
}> = ({ claims, decidedAt, variant = 'row', primaryAxis }) => {
  const cm = useCategoryMeta();
  // Served order, through the ONE resolution — decisionKindsOf below. Read UP HERE, ahead of both
  // arms: `cm.decisionKindHandling` (unlike `cm.categoryKeys`/`cm.label`, which the loading gate
  // below still guards) is safe to read before the gate, exactly like the map `cm.statesApprovedKind`
  // is derived from — it floats in from the warm session cache ahead of the fetch settling, and
  // floats to `{}` on a genuinely cold load, which resolves no kind and is therefore already the
  // honest "nothing yet" shimmer count.
  const kinds = decisionKindsOf(claims, cm.decisionKindHandling);

  // THE LOADING GATE, checked BEFORE either arm (fix, 2026-09-24 review round 1): on a COLD load
  // `decisionKindHandling` is always `{}` until /config resolves (the context's own floor), so
  // `cm.statesApprovedKind` cannot be trusted yet — reading it here would flash the single-kind
  // arm's date on a tenant that does state a Bearbeitung, then swap it for the badge once the
  // real value lands a moment later. A content swap on first paint is exactly what the shimmer
  // posture exists to prevent (UI Layout Stability), so the loading state is decided BEFORE either
  // arm asks its own question, off the one fact that IS already known — whether this row carries a
  // real decision at all (`kinds`, pure over `claims`). A decided row gets the same badge-sized
  // shimmer the multi-kind arm would paint per badge; an undecided row reserves nothing, exactly as
  // both settled arms already do for that case.
  if (cm.configLoading) {
    if (kinds.length === 0) return null;
    return (
      <>
        {kinds.map((kind, i) => (
          <SkeletonBlock
            key={`${kind}-${i}`}
            width={RESOLUTION_PILL_SKELETON_WIDTH}
            height={RESOLUTION_PILL_HEIGHT}
            borderRadius={4}
            style={{ flexShrink: 0 }}
          />
        ))}
      </>
    );
  }

  if (!cm.statesApprovedKind) {
    // A tenant whose served `decisionKindHandling` states at most one Bearbeitung (an absent map
    // reads as `{}`): the approval cannot differ from the classification, so the badge says only
    // THAT the clerk approved — „Genehmigt", in the classification's own hue (the press,
    // 2026-10-28; replaces the 2026-10-19 rule that earned a one-kind tenant no badge for the yes).
    // Any approved decision kind counts here, the filing (`archive`) included: core claims the
    // filing when a letter-flow row is approved without a letter. The DETAIL dates it.
    const approvedAny = (claims ?? []).some(kind => isDecisionClaimKind(kind as DraftKind));
    if (!approvedAny) return null;
    return (
      <>
        {decidedAt && <DecisionDateSpan decidedAt={decidedAt} variant={variant} />}
        <DecisionBadge
          act="approved"
          hue={(primaryAxis && cm.color(primaryAxis)) || theme.colors.navy}
          word={approvedWord(cm.face)}
          variant={variant === 'detail' ? 'detail' : 'row'}
          testId="done-badge"
        />
      </>
    );
  }

  // `kinds` (decisionKindsOf(claims, cm.decisionKindHandling)) was already resolved above, ahead of
  // the loading gate — this branch only reaches here once `cm.configLoading` is settled false, so
  // every entry below has a real vocabulary to read from (the shimmer this branch used to paint
  // per-badge now lives entirely in the loading gate above; folded there rather than kept here too,
  // since past this point `cm.configLoading` can only ever be false and a second check would be dead
  // code).
  const badges = kinds.map((kind, i) => {
    const handling = cm.decisionKindHandling[kind];
    // Defensive, not reachable in practice: `kind` already passed isResolvablePillKind against this
    // same map above, so `handling` is always found here — kept rather than a non-null assertion so
    // a future change to that filter fails safe (no badge) instead of crashing a row.
    if (!handling) return null;
    // The APPROVED handling's present-tense label — the same word the classified badge would wear
    // for this handling, R34. No fallback: every tenant that reaches this branch serves its labels
    // (cm.label floors to the legacy resolver, never to a word this app invents).
    // The press (2026-10-28): the badge says the clerk APPROVED this kind — „Weiterleitung
    // genehmigt" — the served kind word in fixed German, as the bar's „… genehmigen" does.
    const approved = approvedKindWord(kind, cm.face);
    // The DECISION BADGE (small facts on a row, round 4 A.4, operator 2026-10-19): the primary
    // axis's own squared filled shape in the approved handling's served hue, the check as its
    // tinted key — one component with the declined-instead badge (DecisionBadge). It replaced the
    // outlined companion pill of 2026-09-09 and its hover: the word IS the long form, and the
    // classified word a deviation differs from stands one line up on row 2 (house rule: hover only
    // when it reduces load).
    return (
      <DecisionBadge
        key={`${kind}-${i}`}
        act="approved"
        hue={cm.color(handling)}
        word={approved}
        variant={variant === 'detail' ? 'detail' : 'row'}
        testId="done-badge"
      />
    );
  });
  if (badges.length === 0) return null;
  return (
    <>
      {/* The DATE the decision was made, reading directly before the word for what was decided —
          „18.09.26 Weiterleiten". Only where a stamp exists — this arm only reaches here with at
          least one badge already in `badges` (the `badges.length === 0` guard above), so THIS arm's
          date never renders without one; the single-kind arm above is a separate grammar and does
          render its date alone (see its own comment) — the two must not be conflated. The DETAIL is
          the only surface that passes a stamp now (R33) — a list row states the badge undated. */}
      {decidedAt && <DecisionDateSpan decidedAt={decidedAt} variant={variant} />}
      {badges}
    </>
  );
};
